import { action, computed, makeObservable, observable } from 'mobx'

import { LocalStorageKey } from '~/client/src/shared/enums/LocalStorageKey'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'
import InitialState from '~/client/src/shared/stores/InitialState'
import {
  readPerProjectFlag,
  writePerProjectFlag,
} from '~/client/src/shared/utils/perProjectPreference'

// Who gets to see workflow markups, and their label chips, and other workflows' markups.
//
// Lifted out of MapBoxViewerStore whole. It is the one block in that store with no dependency
// on the map at all — no viewport, no style, no mapRef — only the active user and project, so
// it was the store's clearest seam. Three of these flags are per-user localStorage, one is a
// project-wide default on the Project entity, and one is a session override that is never
// persisted; keeping the precedence between them in one file is the point.
export default class WorkflowMarkupsPreferences {
  // The feature gate. Enables the Map Settings rows on the Workflows page;
  // isWorkflowMarkupsAdmin gates the project-default row on top of that.
  @observable public supportsWorkflowMarkups: boolean = false
  @observable public isWorkflowMarkupsAdmin: boolean = false
  // true/false = explicit user choice for the active project, null = unset, meaning fall back
  // to the project default.
  @observable public userMarkupsOverride: boolean | null = null
  // Outranks the stored preference for this map view only, never persisted: a map-first
  // creation shows markups so the user sees context and what they drew.
  @observable public sessionMarkupsOverride: boolean | null = null
  // Workflow-code label chips next to the markups. Pure per-user preference (no project
  // default), default off; only relevant while markups are shown.
  @observable public isWorkflowMarkupLabelsVisible: boolean = false
  // Adjacent (other workflows') markups in the Precise Location modal. Pure per-user
  // preference, default ON; gates only the reference markup layer. Independent of the
  // "Show for me" pref above, and on a separate localStorage key.
  @observable public isAdjacentMarkupsToggleEnabled: boolean = false
  @observable public showAdjacentMarkups: boolean = true

  public constructor(
    private readonly state: InitialState,
    private readonly eventsStore?: EventsStore,
  ) {
    makeObservable(this)
  }

  @action.bound
  public setWorkflowMarkupsSupported(
    isEnabled: boolean,
    isAdmin = false,
  ): void {
    this.supportsWorkflowMarkups = isEnabled
    this.isWorkflowMarkupsAdmin = isEnabled && isAdmin
    if (isEnabled) this.loadWorkflowMarkupsPref()
  }

  @action.bound
  public loadWorkflowMarkupsPref(): void {
    const stored = this.readPref(LocalStorageKey.WorkflowMarkupsVisible)
    this.userMarkupsOverride = stored ?? null
    this.sessionMarkupsOverride = null
    // Off unless the user explicitly turned it on for this project — both booleans are
    // persisted, so an opt-in survives and an opt-out is a no-op.
    this.isWorkflowMarkupLabelsVisible =
      this.readPref(LocalStorageKey.WorkflowMarkupLabelsVisible) === true
  }

  // Enables the Precise Location "Show adjacent" row and loads its pref.
  @action.bound
  public setAdjacentMarkupsToggleEnabled(isEnabled: boolean): void {
    this.isAdjacentMarkupsToggleEnabled = isEnabled
    if (isEnabled) this.loadAdjacentMarkupsPref()
  }

  // Unset ⇒ ON (adjacent markups show by default).
  @action.bound
  public loadAdjacentMarkupsPref(): void {
    this.showAdjacentMarkups =
      this.readPref(LocalStorageKey.ShowAdjacentMarkups) ?? true
  }

  // User toggle: flips MY adjacent-markups visibility and persists it as a per-project
  // localStorage preference (separate key from "Show for me").
  @action.bound
  public toggleShowAdjacentMarkups(): void {
    const next = !this.showAdjacentMarkups
    this.showAdjacentMarkups = next
    this.writePref(LocalStorageKey.ShowAdjacentMarkups, next)
  }

  // Effective visibility: the session override wins, then the user's explicit choice;
  // otherwise shown unless the project default disables it (absent/false = shown).
  @computed
  public get showWorkflowMarkups(): boolean {
    if (this.sessionMarkupsOverride != null) return this.sessionMarkupsOverride
    if (this.userMarkupsOverride != null) return this.userMarkupsOverride
    return !this.state.activeProject?.areWorkflowMarkupsDisabledByDefault
  }

  @action.bound
  public forceWorkflowMarkupsVisible(): void {
    this.sessionMarkupsOverride = true
  }

  @action.bound
  public clearSessionMarkupsOverride(): void {
    this.sessionMarkupsOverride = null
  }

  // User toggle (Map Settings switch + the "A" key): flips MY visibility and persists it as a
  // per-project localStorage preference. No DB write.
  @action.bound
  public toggleWorkflowMarkups(): void {
    const next = !this.showWorkflowMarkups
    // Explicit intent outranks the session override, or the switch reads as dead.
    this.sessionMarkupsOverride = null
    this.userMarkupsOverride = next
    this.writePref(LocalStorageKey.WorkflowMarkupsVisible, next)
  }

  // User toggle: flips MY workflow-code label chips and persists it as a per-project
  // localStorage preference (same per-user scope as the markups).
  @action.bound
  public toggleWorkflowMarkupLabels(): void {
    const next = !this.isWorkflowMarkupLabelsVisible
    this.isWorkflowMarkupLabelsVisible = next
    this.writePref(LocalStorageKey.WorkflowMarkupLabelsVisible, next)
  }

  // Admin-only: the project-wide default, persisted on the Project entity rather than
  // localStorage, which is why this is the only one that dispatches.
  @action.bound
  public toggleWorkflowMarkupsProjectDefault(): void {
    const project = this.state.activeProject
    if (!project?.id) return
    project.areWorkflowMarkupsDisabledByDefault =
      !project.areWorkflowMarkupsDisabledByDefault
    this.eventsStore?.dispatch(e.SAVE_PROJECT, project)
  }

  private readPref(key: LocalStorageKey): boolean | undefined {
    return readPerProjectFlag(
      this.state.user?.id,
      this.state.activeProject?.id,
      key,
    )
  }

  private writePref(key: LocalStorageKey, value: boolean): void {
    writePerProjectFlag(
      this.state.user?.id,
      this.state.activeProject?.id,
      key,
      value,
    )
  }
}
